import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Loader, Stack, Stepper, Title } from "@mantine/core";
import { ShieldCheckIcon, TruckIcon, UserIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm, UseFormProps } from "react-hook-form";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast } from "react-toastify";
import { z } from "zod";

import { SideBySide } from "@/components/@custom/SideBySide";
import { IMPLEMENT_TYPES } from "@/constants";
import { Step } from "@/features/auth/components";
import { useWindowLocationHost } from "@/hooks/useWindowLocationHost";
import { GLOBAL_SCHEMA } from "@/lib/zod";
import { useAuth, useGetCompanyByHostQuery, useLazyGetIsTruckQuery } from "@/store/apis/nextcargo";
import { useLazyGetTruckBrandsQuery } from "@/store/apis/nextcargo/truck/brands";
import { useLazyGetTruckModelsByBrandQuery } from "@/store/apis/nextcargo/truck/models";
import { cn } from "@/utils/cn";
import { cva } from "@/utils/cva.config";
import { transformToOption } from "@/utils/transformToOption";

type ImplementType = typeof IMPLEMENT_TYPES;

export type RegisterSchema = z.infer<typeof registerSchema>;
const registerSchema = z.object({
  user: z.object({
    name: GLOBAL_SCHEMA.NAME.max(80, "Tamanho máximo de 80 caracteres"),
    cpf: GLOBAL_SCHEMA.CPF,
    cnh: GLOBAL_SCHEMA.CNH,
    birthDate: GLOBAL_SCHEMA.BIRTHDAY,
    phone: GLOBAL_SCHEMA.PHONE,
    email: GLOBAL_SCHEMA.EMAIL
  }),
  truck: GLOBAL_SCHEMA.TRUCK
});

export const Route = createFileRoute("/_auth/app/registro/$id")({
  params: {
    parse: ({ id }) => {
      return { id: +id };
    }
  },
  component: RegisterDriver,
  head: () => ({
    meta: [{ title: "Registro - App" }]
  })
});

function RegisterDriver() {
  const { id } = Route.useParams();
  const { windowLocationHost } = useWindowLocationHost();
  const { SignUpDriver } = useAuth();

  const { data: company } = useGetCompanyByHostQuery({ host: windowLocationHost }, { skip: !windowLocationHost });
  const [getIsTruck] = useLazyGetIsTruckQuery();
  const [getTruckBrands, { data: brands, isLoading: isBrandsLoading }] = useLazyGetTruckBrandsQuery({
    selectFromResult: ({ data, ...state }) => {
      const innerData = data || [];
      const dataAsOption = transformToOption(
        innerData,
        (item) => item.id,
        (item) => item.name
      );

      return {
        ...state,
        data: {
          original: innerData,
          asProp: {
            data: dataAsOption,
            isLoading: state.isLoading,
            isError: state.isError
          },
          options: dataAsOption
        }
      };
    }
  });
  const [getModelsByBrand, { data: models, isLoading: isModelsLoading }] = useLazyGetTruckModelsByBrandQuery({
    selectFromResult: ({ data, ...state }) => {
      const innerData = data || [];
      const dataAsOption = transformToOption(
        innerData,
        (item) => item.id,
        (item) => item.name
      );

      return {
        ...state,
        data: {
          original: innerData,
          asProp: {
            data: dataAsOption,
            isLoading: state.isLoading,
            isError: state.isError
          },
          options: dataAsOption
        }
      };
    }
  });

  const [activeStep, setActiveStep] = useState(0);
  const [submitStatus, setSubmitStatus] = useState({ success: false, error: false });

  const implementsTypes = IMPLEMENT_TYPES.map((type) => ({ value: type.type, label: type.type }));

  const FORM_PROPS: UseFormProps<RegisterSchema> = {
    resolver: zodResolver(registerSchema),
    mode: "all",
    defaultValues: {
      user: {
        name: "",
        cpf: "",
        cnh: "",
        birthDate: "",
        phone: "",
        email: ""
      },
      truck: {
        plate: "",
        renavam: "",
        brand: "",
        model: "",
        color: "",
        implementType: "",
        shaftQuantity: 0,
        grossCapacity: 0,
        netCapacity: 0
      }
    }
  };
  const methods = useForm<RegisterSchema>(FORM_PROPS);

  const brand = methods.watch("truck.brand");
  const implementType = methods.watch("truck.implementType") as ImplementType[number]["type"];
  const grossCapacity = methods.watch("truck.grossCapacity");
  const shaftQuantity = methods.watch("truck.shaftQuantity");
  const netCapacity = methods.watch("truck.netCapacity");

  const valuesForm = methods.getValues();

  const modelLabel = useMemo(() => getLabelelByValue({ arr: models?.options || [], value: valuesForm.truck.model }), [valuesForm.truck.model]);
  const brandLabel = useMemo(() => getLabelelByValue({ arr: brands?.options || [], value: valuesForm.truck.brand }), [valuesForm.truck.brand]);

  const companyLogo = company?.logo && `${import.meta.env.VITE_APP_API_URL}/${company?.logo}`;

  const onSubmit: SubmitHandler<RegisterSchema> = async (data) => {
    const { user, truck } = data;

    const { isSuccess, isError } = await getIsTruck({ plate: truck.plate });

    if (!isSuccess && isError) {
      setActiveStep(1);
      methods.setFocus("truck.plate");
      methods.setError("truck.plate", { message: "Placa inválida" });
      return;
    }

    nextStep();
    const contact = [
      {
        id: null,
        name: user.name.toUpperCase(),
        fone: user.phone,
        email: user.email
      }
    ];

    const { error } = await SignUpDriver({
      name: user.name.toUpperCase(),
      cpf: user.cpf,
      cnh: user.cnh,
      birthDate: user.birthDate,
      isEnabled: true,
      company: { id },
      contact,
      truck: [
        {
          id: null,
          isEnabled: true,
          name: user.name.toUpperCase(),
          owner: user.name.toUpperCase(),
          licensePlate: truck.plate,
          renavam: truck.renavam,
          brand: {
            id: truck.brand
          },
          vehicleModel: {
            id: truck.model
          },
          color: truck.color,
          implementType: truck.implementType,
          grossCapacity: truck.grossCapacity,
          shaftQuantity: truck.shaftQuantity,
          netCapacity: truck.netCapacity,
          company: {
            id: String(id)
          },
          contact
        }
      ]
    });

    if (!error) {
      setSubmitStatus((prev) => ({ ...prev, success: true }));
      return;
    }
    setSubmitStatus((prev) => ({ ...prev, error: true }));

    // if (message.includes(`Esse renavam não corresponde a placa ${methods.getValues("truck.plate")}!`)) {
    //   setActiveStep(1);
    //   methods.setValue("truck.renavam", "");
    //   methods.trigger("truck.renavam");
    // }
  };

  const onInvalid: SubmitErrorHandler<RegisterSchema> = (_err) => {
    toast.error("Alguns campos não estão devidamente preenchidos!");
  };

  function getLabelelByValue<T extends { value: string; label: string }>({ arr = [], value }: { arr: T[]; value: string }) {
    const found = arr.find((item) => item.value === value);
    if (!found || arr.length === 0) {
      console.error("RegisterDriver::getLabelelByValue::ERROR: Modelo não encontrado!");
      return "";
    }

    return found.label;
  }

  const stepFields = ["user", "truck"] as const;
  const validateCurrentStep = async () => {
    if (activeStep >= stepFields.length) return true;

    const isValid = await methods.trigger(stepFields[activeStep]);
    return isValid;
  };
  const nextStep = async () => {
    const isValid = await validateCurrentStep();

    if (!isValid) {
      toast.error("Pra avançar para o próximo passo, preencha os campos corretamente!");
      return;
    }
    setActiveStep((prev) => (prev < 3 ? prev + 1 : prev));
  };
  const prevStep = () => setActiveStep((prev) => (prev > 0 ? prev - 1 : prev));

  useEffect(() => {
    if (activeStep === 1) {
      getTruckBrands(undefined, true);
    }
  }, [activeStep]);

  useEffect(() => {
    if (!brand) return;

    methods.trigger("truck.model");
    methods.setFocus("truck.model");
    methods.setValue("truck.model", "");

    getModelsByBrand({ id: Number(brand) });
  }, [brand]);

  useEffect(() => {
    if (!!grossCapacity || !!shaftQuantity || !!netCapacity) return;
    function findImplementType(shaftQuantity: number, grossCapacity: number, netCapacity: number): ImplementType[number]["type"] | undefined {
      const found = IMPLEMENT_TYPES.find((item) => item.shaftQuantity === shaftQuantity && item.grossCapacity === grossCapacity && item.netCapacity === netCapacity);
      return found?.type;
    }

    const found = findImplementType(shaftQuantity, grossCapacity, netCapacity);
    if (!found) return;
    methods.setValue("truck.implementType", found);
    methods.clearErrors("truck.implementType");
  }, [grossCapacity, shaftQuantity, netCapacity]);

  useEffect(() => {
    if (!implementType) return;

    const findInplementType = (type: ImplementType[number]["type"]) => {
      return IMPLEMENT_TYPES.find((item) => item.type === type);
    };

    const found = findInplementType(implementType);
    if (!found) return;
    methods.setValue("truck.shaftQuantity", found.shaftQuantity);
    methods.setValue("truck.grossCapacity", found.grossCapacity);
    methods.setValue("truck.netCapacity", found.netCapacity);
  }, [implementType]);

  // const [modalPlate, setModalPlate] = useState(false);
  // useEffect(() => {
  //   if (modalPlate) {
  //     modals.openContextModal({
  //       modal: "plateNotValid",
  //       withCloseButton: false,
  //       innerProps: {
  //         plate: "ABC1234"
  //       }
  //     });
  //   }
  // }, [modalPlate]);

  /* ############################################################ */

  return (
    <>
      <SideBySide.Primary>
        {companyLogo && <img src={companyLogo} className={hiddenOnMobile({ className: "mb-2 w-[300px]" })} />}

        <Card component={Stack} className="w-[min(100%,var(--container-xl))]" bg="white" gap="md" shadow="md" radius="lg" withBorder>
          <Title order={1} c="primary" className="block! md:hidden!">
            Cadastro de Motorista
          </Title>

          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
              <Stepper active={activeStep} onStepClick={setActiveStep} size="sm" classNames={{ steps: "!hidden md:!flex " }} wrap={false}>
                <Stepper.Step
                  label="Primeiro passo"
                  description="Inserir dados pessoais"
                  icon={<UserIcon weight="bold" />}
                  color={methods.getFieldState("user").invalid ? "red" : ""}
                >
                  <Step.One />
                </Stepper.Step>

                <Stepper.Step
                  label="Segundo passo"
                  description="Inserir dados do veículo"
                  icon={<TruckIcon weight="bold" />}
                  color={methods.getFieldState("truck").invalid ? "red" : ""}
                  loading={isBrandsLoading || isModelsLoading}
                >
                  <Step.Two brands={brands.asProp} models={models.asProp} implementsTypes={implementsTypes} />
                </Stepper.Step>

                <Stepper.Step label="Terceiro passo" description="Revisar dados" icon={<ShieldCheckIcon weight="bold" />} loading={methods.formState.isSubmitting}>
                  <Step.Three brandLabel={brandLabel} modelLabel={modelLabel} />
                </Stepper.Step>

                <Stepper.Completed>
                  <Step.Four success={submitStatus.success} error={submitStatus.error} />
                </Stepper.Completed>
              </Stepper>

              <div className="my-3 flex justify-between">
                {(activeStep !== 0 && activeStep !== 3) || submitStatus.error ? (
                  <Button variant="transparent" onClick={prevStep} leftSection={<MdKeyboardArrowLeft />} size="sm">
                    Voltar
                  </Button>
                ) : null}

                {activeStep < 2 && (
                  <Button onClick={nextStep} rightSection={<MdKeyboardArrowRight />} className={cn([activeStep === 0 && "ml-auto"])} size="sm">
                    {activeStep < 1 && "Próximo"}
                    {activeStep === 1 && "Revisar"}
                  </Button>
                )}

                {activeStep === 2 && (
                  <Button type="submit" size="sm" className="uppercase!" loading={methods.formState.isSubmitting}>
                    Concluir
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </Card>
      </SideBySide.Primary>

      <SideBySide.Secondary>
        {companyLogo && <img src={companyLogo} className={"w-[400px] xl:w-[550px]"} />}
        {!companyLogo && <Loader className={hiddenOnMobile({ className: "mb-2" })} />}
      </SideBySide.Secondary>
    </>
  );
}

const hiddenOnMobile = cva({
  base: "block md:hidden"
});
